import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  CodeRangeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApplyLesserOfChargesPropagationMutation,
  useGetLesserOfChargesByIdQuery,
  useLazyGetLesserOfChargesPropagationPreviewQuery,
  useUpdateLesserOfChargesMutation,
} from "store/queries/lesserOfCharges";

import {
  ILesserOfChargesFormData,
  INITIAL_LESSER_OF_CHARGES_FORM_DATA,
  LesserOfChargesForm,
} from "./components/LesserOfChargesForm";
import { groupedRulesetsForSubmit } from "./components/LesserOfChargesGroupedRules";
import { usePropagateAfterEdit } from "./components/usePropagateAfterEdit";

export function EditLesserOfChargesPage() {
  useDocumentTitle("Edit Lesser-Of Charges Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetLesserOfChargesByIdQuery(
    id ?? ""
  );
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateLesserOfChargesMutation();
  const [formData, setFormData] = useState<ILesserOfChargesFormData>(
    INITIAL_LESSER_OF_CHARGES_FORM_DATA
  );

  const { openForId, modal: propagateModal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetLesserOfChargesPropagationPreviewQuery as any,
    useApplyPropagation: useApplyLesserOfChargesPropagationMutation as any,
    onDone: () => navigate("/payer-rules"),
  });

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      scope: ruleset.scope,
      multiplier: Number(ruleset.multiplier),
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      // The backend stores codes flat; fold consecutive runs back into ranges
      // so the editor shows "0272-0420" rather than 149 individual chips.
      codes: collapseToRanges(ruleset.codes ?? []),
      excludedCodes: collapseToRanges(ruleset.excludedCodes ?? []),
      groupName: ruleset.groupName ?? "",
      // CLE-2524: always present on loaded rows (backfilled).
      applicability: ruleset.applicability,
      groupedRulesets: ruleset.groupedRulesets ?? [],
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    if (!formData.scope) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    let codes: string[];
    let excludedCodes: string[];
    try {
      codes = prepareCodesForSubmit(formData.codes);
      excludedCodes = prepareCodesForSubmit(formData.excludedCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        scope: formData.scope,
        multiplier: formData.multiplier,
        codes,
        excludedCodes,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: immutable — the locked form value matches the stored row.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        // CLE-2597 — a FALLBACK rule may name a payer; only a TYPICAL template is scopeless.
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
        // CLE-3058 — [] clears saved groups (e.g. after switching to Total charges).
        groupedRulesets: groupedRulesetsForSubmit(
          formData.groupedRulesets,
          formData.scope,
          formData.applicability
        ),
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Lesser-Of charges rule updated successfully.",
      });
      if (formData.applicability !== "SCOPED") {
        await openForId(id);
      } else {
        navigate("/payer-rules");
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to update lesser-of charges rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <LesserOfChargesForm
        title="Edit Lesser-Of Charges Rule"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      {propagateModal}
      <RuleHistory histories={data?.data.history} />
    </>
  );
}
